import FieldProto from "../../../fields3/proto/field-proto"
import Filter from "./filter"
import ItemsCollection from "../../../model2/item/items-collection"
import QuickSearch from "../../../gd-modules/manager/quick-search/quick-search"
import FieldsCollection from "../../../fields3/collection/base/fields-collection"
import {EntityType, EntityTypeDict} from "../../../gd/const/item"
import {GroupedData} from "../../../data-structures/group"
import {FilterCollectionType, FiltersGroup} from "./@types"
import Collection from "../../../data-structures/collection"
import initValueForFilterV5_fromOldFormat from "./converter-v1-v5"
import {SortDirection} from "../../../data-structures/sort"
import {BUS_FILTERS_UPDATE} from "../../../../layout/app-controls/buttons/filter/@const"
import {ItemDisplay} from "../../../model2/item/@type"
import ItemModel from "../../../model2/item/item-model";

// const debug = "FiltersManager"
const debug = false

/**
 * Manages filters for data filtering operations in version 5 of the filtering system.
 * Handles field-based filters, quick search, and custom fields for different entity types.
 */
export default class FiltersManager {

    public collectionType       : FilterCollectionType = FilterCollectionType.ITEMS_COLLECTION

    /** Collection of field collections for different entity types */
    public fieldsCollections    : FieldsCollection[]

    /** Array of active filter instances */
    public filters              : Filter[] = []

    /** Quick search functionality for instant filtering */
    public quickSearch          : QuickSearch

    /**
     * Creates a new FiltersManager instance
     * @param fieldsCollections - Collections of fields for different entity types (gd.fields.task, gd.fields.project)
     * @param initValues - Initial filter values to apply
     */
    constructor(fieldsCollections: FieldsCollection[], initValues = {}) {

        this.fieldsCollections  = fieldsCollections

        this.quickSearch        = new QuickSearch(this.onChange.bind(this))

        // Using cloneDeep to avoid shared references to filter objects (e.g., for numbers),
        // ensuring that changes do not directly mutate the original settings.
        this.initFilters(initValues)

        // update counter
        gd.bus.triggerEvent(BUS_FILTERS_UPDATE, {counter: this.filters.length})
    }

    /**
     * Initializes the filters array based on the provided initial values.
     * This method clones the initial values to avoid mutating the original object,
     * then iterates over all available fields to determine if an initial value exists
     * for each field. If so, it adds a filter for that field.
     *
     * @param initValues - The initial filter values (possibly in legacy or V5 format)
     */
    initFilters(initValues: any) {
        // Reset the filters array to ensure a clean start
        this.filters = []

        // Deep clone the initial values to prevent side effects from mutations
        const values = _.cloneDeep(initValues)

        debug && console.log(debug, "initFilters", values)

        // Iterate over all fields available in the manager
        _(this.fields)
            .orderBy([
                (field: FieldProto) => {
                    return field.isCustom ? 1 : 0
                },
                (field: FieldProto) => {
                    return field.isCustom ? field.label.toLowerCase() : ''
                },
            ], [
                SortDirection.ASC,
                SortDirection.ASC
            ])
            .map((field: FieldProto) => {
                // Attempt to get the initial value for this field by its systemFieldId
                let initValue = _.get(values, field.systemFieldId, null)

                // If not found, try to convert from an old format (legacy) value
                if (!initValue) {
                    initValue = initValueForFilterV5_fromOldFormat(values, field)
                }

                debug && console.log(debug, "initFilters initValue", initValue, field)

                // If an initial value exists (after conversion if needed), add a filter for this field
                if (initValue) {
                    this.addFieldFilter(field, initValue)
                }
            })
            .value()

        debug && console.log(debug, "initFilters filters", this.filters)
    }

    /**
     * Adds a new filter based on a field
     * @param field - The field to create a filter for
     * @param initValue - Initial value for the filter
     * @returns The created filter instance or null if not applicable
     */
    addFieldFilter(field: FieldProto, initValue?: any): Filter | null {
        debug && console.log(debug, "addFieldFilter", field.filterType, field, initValue)

        if (field.filterType && field.isFilterAvailable) {
            const filter = new Filter(this, field, initValue)
            this.filters.push(filter)
            return filter
        }

        return null
    }

    /**
     * Removes a specific filter from the filters array
     * @param filter - The filter to remove
     */
    removeFilter(filter: Filter) {
        const removedFilters = _.remove(this.filters, filter)
        debug && console.log(debug, "removeFilter", removedFilters, filter, this.filters)
        this.onChange()
    }

    /**
     * Returns all current filter instances.
     * Can be overridden to provide additional or custom filters.
     * @returns Array of all filter instances.
     */
    getFilters() {
        return this.filters
    }

    /**
     * Returns only the active filters (filters with conditions set).
     * @returns Array of active filter instances
     */
    get activeFilters() {
        return _.filter(this.filters, (filter: Filter) => {
            return !filter.isEmpty
        })
    }

    /**
     * Groups the current filters by their associated entity type.
     *
     * - Filters out any filters that do not have a field assigned.
     * - Groups the remaining filters by the field's entityType.
     * - Converts the grouped object into an array of [entityType, filters[]] pairs.
     * - Maps each group into a FiltersGroup object, including a label from EntityTypeDict.
     * - Orders the groups by entityType in ascending order.
     *
     * @returns {FiltersGroup[]} Array of filter groups, each grouped by entity type.
     */
    get filtersGroupedByEntityType(): FiltersGroup[] {
        return _(this.filters)
            // Only include filters that have a field assigned
            .filter((filter: Filter) => {
                return !!filter.field
            })
            // Group filters by their field's entityType
            .groupBy((filter: Filter) => {
                return filter.field?.entityType
            })
            // Convert the grouped object into an array of [entityType, filters[]] pairs
            .toPairs()
            // Map each group into a FiltersGroup object
            .map((group): FiltersGroup => {
                // group[0] is the entityType, group[1] is the array of filters
                const entityType = group[0] as unknown as EntityType
                return {
                    entityType  : entityType,
                    label       : EntityTypeDict[entityType as keyof { }], // Get the label for the entityType from EntityTypeDict
                    filters     : group[1]
                }
            })
            .orderBy(group => group.entityType, SortDirection.ASC)
            .value()
    }

    /**
     * Gets quick filter options (to be overridden)
     * Options have an onClick parameter where specific filters should be described and added to this manager.
     * @returns Array of quick filter options
     */
    get quickFilters(): Option[] {
        return []
    }

    /**
     * Gets all fields from all field collections
     * @returns Array of all field prototypes
     */
    get fields(): FieldProto[] {
        let res: FieldProto[] = []
        _.map(this.fieldsCollections, (fieldsCollection: FieldsCollection) => {
            res = res.concat([...fieldsCollection.models])
        })
        return res
    }

    /**
     * Gets field structures organized by entity type
     * @returns Object mapping entity types to grouped field data
     */
    get fieldsStructures(): {[key in EntityType]?: GroupedData} {
        const res: {[key in EntityType]?: GroupedData} = {}
        // should be overridden
        return res
    }

    /**
     * Gets the items collection (should be overridden to get actual view itemsCollection)
     * Views like Table or List use ItemsCollection
     * @returns Default items collection instance
     */
    get itemsCollection(): any /*ItemsCollection -> ItemsCollectionV2*/{
        return new ItemsCollection()
    }

    /**
     * Gets the models collection (should be overridden to get actual view Collection)
     * GList uses models collection
     * @returns Default models collection instance
     */
    get modelsCollection() {
        return new Collection()
    }

    /**
     * Gets the fields collection for a specific entity type
     * @param entityType - The entity type to get fields for
     * @returns The fields collection for the specified entity type
     */
    getEntityFieldsCollection(entityType: EntityType) {
        return _.find(this.fieldsCollections, (fieldsCollection: FieldsCollection) => {
            return fieldsCollection.entityType === entityType
        }) as unknown as FieldsCollection
    }

    /**
     * Resets all filters to empty state
     */
    reset() {
        this.filters = []
        this.onChange()
    }

    /**
     * Retrieves the IDs of items that have been quick filtered.
     * This method is intended to be overridden in subclasses.
     * Items may be prefiltered, and this method determines which items remain visible
     * before further filtering by this manager.
     * @returns {ID[] | null} An array of item IDs or null if not applicable.
     */
    getQuickFilteredItemsIds(): ID[] | null {
        // to redefine
        return null
    }

    /**
     * Handles changes to filters, such as when a filter is added, removed, or updated.
     * Optionally accepts an updatedFilter, which will have its loading state managed during the update.
     * Triggers the BUS_FILTERS_UPDATE event to notify listeners of filter changes.
     * Calls the onChangeFilters callback if defined, allowing for custom actions (e.g., data reload).
     *
     * @param updatedFilter - (Optional) The filter that was updated, to manage its loading state.
     */
    async onChange(updatedFilter?: Filter | null) {
        debug && console.log(debug, "onChange", updatedFilter)

        // If an updated filter is provided, set its loading state and trigger update event
        if (updatedFilter) {
            updatedFilter.isDataLoading = true
            gd.bus.triggerEvent(BUS_FILTERS_UPDATE, { counter: this.activeFilters.length })

            // Await any custom onChangeFilters callback (e.g., reload data)
            await this.onChangeFilters?.()

            // Reset the loading state after update
            updatedFilter.isDataLoading = false
        }

        // Always trigger the update event after changes
        gd.bus.triggerEvent(BUS_FILTERS_UPDATE, { counter: this.activeFilters.length })
    }

    /**
     * Filters an array of models based on quick search, active filters, and quick filter IDs.
     *
     * @param models - The array of models to filter.
     * @returns The filtered array of models that pass all filter criteria.
     */
    filterModels(models: any[]) {
        // Get the currently active filters
        const filters       : Filter[]    = this.getFilters()

        debug && console.log(debug, "filterModels", filters)

        // Quick search state and value
        const quickSearchEnabled            : boolean       = this.quickSearch.enabled
        const quickSearchValueNormalized    : string        = this.quickSearch.searchValueNormalized

        // IDs of items that passed any external quick filtering (may be null)
        const quickFilterVisibleIds         : RID[] | null  = this.getQuickFilteredItemsIds()

        // Filter the models array
        return _.filter(models, (model:any)=>{
            // Flag to determine if the model should be filtered out (hidden)
            let isFilteredOut : boolean = false // true when item doesn't match current filters i.e. we want to hide it?

            /**--- Quick search [START] ---**/
            // If quick search is enabled, filter out models that do not match the search value
            if (quickSearchEnabled) {
                if (!model.quickSearch(quickSearchValueNormalized)) {
                    isFilteredOut = true
                }
            }
            /**--- Quick search [END] ---**/

            /**--- Filters [START] ---**/
            // Apply each filter to the model; if any filter fails, mark as filtered out
            _.some(filters, (filter: Filter)=>{
                const filterResult = filter.filterModel(model)
                if (!filterResult) {
                    isFilteredOut = true
                    return true // stop iterating further filters
                }
                return false
            })
            /**--- Filters [END] ---**/

            /**--- Quick filter [START] ---**/
            // If quickFilterVisibleIds is defined, filter out models whose id is not in the list
            if (!isFilteredOut &&
                quickFilterVisibleIds &&
                !quickFilterVisibleIds.includes(model.id)) {
                isFilteredOut = true
            }
            /**--- Quick filter [END] ---**/

            // Only include models that are not filtered out
            return !isFilteredOut
        })
    }

    /**
     * Applies filters to an items collection and returns items to hide
     * @param itemsCollection - The collection of items to filter
     * @returns Array of item IDs that should be hidden
     */
    filterItemsCollection(itemsCollection: ItemsCollection) {
        const itemsToHide   : RID[]         = []
        const filters       : Filter[]    = this.getFilters()

        const quickSearchEnabled            : boolean       = this.quickSearch.enabled
        const quickSearchValueNormalized    : string        = this.quickSearch.searchValueNormalized
        const quickFilterVisibleIds         : RID[] | null  = this.getQuickFilteredItemsIds()

        debug && console.log(debug, "filterItemsCollection", quickSearchEnabled, quickSearchValueNormalized, quickFilterVisibleIds, filters)

        _.map(itemsCollection.models, (itemModel: ItemModel)=>{

            let isFilteredOut : boolean = false; // true when item doesn't match current filters i.e. we want to hide it?

            /**--- Quick search [START] ---**/
            if (quickSearchEnabled) {
                if (!itemModel.item.quickSearch(quickSearchValueNormalized)) {
                    isFilteredOut = true
                    itemsToHide.push(itemModel.id)
                }
            }
            /**--- Quick search [END] ---**/

            /**--- Filters [START] ---**/
            if (!isFilteredOut) {
                _.some(filters, (filter: Filter)=>{
                    const filterResult = filter.filterItemModel(itemModel)
                    if (!filterResult) {
                        itemsToHide.push(itemModel.id)
                        return true
                    }

                    return false
                })
            }
            /**--- Filters [START] ---**/

            /**--- Quick filter [START] ---**/
            // filter out items ids that were filtered outside this manager if any
            if (!isFilteredOut &&
                quickFilterVisibleIds &&
                !quickFilterVisibleIds.includes(itemModel.id)) {
                itemsToHide.push(itemModel.id)
            }
            /**--- Quick filter [END] ---**/
        })

        return itemsToHide
    }

    /**
     * Enables visible ghosts for items in a tree structure
     * Marks parent items as visible ghost if they have visible children
     * @param itemsCollection - The collection of items to process
     */
    enableVisibleGhosts(itemsCollection: ItemsCollection) {
        // Mark parent items as not hidden if item has visible children

        debug && console.log(debug, 'enableVisibleGhosts', itemsCollection.treeRoots, itemsCollection)

        // DebugPerformance.start("enableVisibleGhosts")

        /**
         * Recursively enables visible ghosts for items in a tree structure
         * @param item - The item to process
         * @returns The display state of the item
         */
        const _recursivelyEnableVisibleGhosts = (item: ItemModel) => {
            debug && console.log(debug, item.debugName, item.display, item)
            // item.debugGantt()

            let display = item.display
            let needUpdate = false

            _.map(item.treeChildren, (childItem: ItemModel)=>{
                let childDisplay = _recursivelyEnableVisibleGhosts(childItem)

                // tak i ne ponyal chto eto ... ideya v dereve sdelat' filter by project type gde same type
                // vnutri dereva
                if (
                    childDisplay === ItemDisplay.GHOST_HIDDEN
                    && (display === ItemDisplay.VISIBLE || display === ItemDisplay.GHOST_VISIBLE)
                ) {
                    childDisplay = ItemDisplay.GHOST_VISIBLE
                    childItem.display = ItemDisplay.GHOST_VISIBLE
                }

                // child visible parent not
                if (childDisplay === ItemDisplay.VISIBLE && display === ItemDisplay.HIDDEN) {
                    needUpdate = true
                    display = ItemDisplay.GHOST_VISIBLE
                }

                // child ghost - parent hidden
                if (childDisplay === ItemDisplay.GHOST_VISIBLE && display === ItemDisplay.HIDDEN) {
                    needUpdate = true
                    display = ItemDisplay.GHOST_VISIBLE
                }
            })

            if (needUpdate) {
                debug && console.orange(item.debugName, "done. NeedUpdate", display)
                item.display = display
            } else {
                debug && console.orange(item.debugName, "done. No change.")
            }

            const result = display
            debug && console.print("Result", display)
            return result

        }

        _.map(itemsCollection.treeRoots, (rootItem: ItemModel)=>{
            debug && console.log("rootItem=", rootItem)
            _recursivelyEnableVisibleGhosts(rootItem)
            // console.print("rootDates=", childrenDates.start, childrenDates.end)
        })


        // DebugPerformance.stop("enableVisibleGhosts", 2)
    }

    /**
     * Called when filters are changed by the user or programmatically.
     * This method should be overridden in subclasses to provide custom logic
     * for handling filter changes.
     * By default, it does nothing.
     */
    async onChangeFilters() {
        // should be overridden in subclasses to handle filter changes
    }

    /**
     * Filters items according to the current filter configuration.
     * This method should be overridden in subclasses to provide specific filtering logic.
     * By default, it does nothing.
     */
    filterItems() {
        /* should be overridden */
    }

    /**
     * Export current filters to a plain object representation.
     * This is used for serializing the current filter state for saving.
     */
    toJSON() {
        const result: any = {}

        // Iterate over all filters in this.filters array
        this.filters.map(filter => {
            // Get the systemFieldId from the filter's field, if it exists
            const systemFieldId = filter.field?.systemFieldId

            // Only include filters that are not empty and have a valid systemFieldId
            // This avoids exporting filters that are not set or not applicable
            if (!filter.isEmpty && systemFieldId) {
                // Use the systemFieldId as the key and the filter's object representation as the value
                result[systemFieldId as keyof {}] = filter.toObject()
            }
        })

        // Use lodash's cloneDeep to avoid shared references to filter objects (e.g., for numbers),
        // ensuring that changes to the returned object do not directly mutate the original settings.
        return _.cloneDeep(result)
    }
}
